import React, {useCallback, useEffect, useState} from "react";
import {listSites, pauseSite, saveSiteSettings} from "../api/admin";

const hhmm = (t) => (t || "").slice(0, 5);

/** Per listing: who covers it, what's happening now, and its live-tour rules (radius, hours, queue, pause). */
const SitesTable = ({onAuthError}) => {
    const [sites, setSites] = useState(null);
    const [query, setQuery] = useState("");
    const [error, setError] = useState("");
    const [editing, setEditing] = useState(null);
    const [busy, setBusy] = useState(null);

    const load = useCallback(async (q) => {
        setError("");
        try {
            setSites(await listSites(q));
        } catch (err) {
            if (!(await onAuthError(err))) setError(err.message);
        }
    }, [onAuthError]);

    useEffect(() => {
        const id = setTimeout(() => load(query.trim()), 300);
        return () => clearTimeout(id);
    }, [query, load]);

    const save = async (site, settings, call = saveSiteSettings) => {
        setBusy(site.siteId);
        setError("");
        try {
            await call(site.siteType, site.siteId, settings);
            setEditing(null);
            await load(query.trim());
        } catch (err) {
            if (!(await onAuthError(err))) setError(err.message);
        } finally {
            setBusy(null);
        }
    };

    const togglePause = (s) => save(s, !s.settings.toursPaused, pauseSite);

    return (
        <div>
            <div className="brk-card__head">
                <input className="brk-input" style={{maxWidth: 320}} type="search" placeholder="Search listings by name or area"
                       aria-label="Search listings" value={query} onChange={(e) => setQuery(e.target.value)}/>
                <button type="button" className="brk-btn brk-btn--ghost brk-btn--sm" onClick={() => load(query.trim())}>Refresh</button>
            </div>
            {error && <div className="brk-notice brk-notice--error" role="alert">{error}</div>}
            {!sites ? <p className="brk-empty">Loading…</p> : sites.length === 0 ? <p className="brk-empty">No listings match.</p> : (
                <div className="brk-table-wrap">
                    <table className="brk-table">
                        <thead>
                        <tr><th>Listing</th><th>Agents</th><th>Now</th><th>Live tours</th><th/></tr>
                        </thead>
                        <tbody>
                        {sites.map((s) => (
                            <tr key={`${s.siteType}-${s.siteId}`}>
                                <td>
                                    <div className="brk-strong">{s.name}</div>
                                    <div className="brk-hint">{s.location}</div>
                                    {s.builderOnly && <span className="brk-badge brk-badge--grey">Builder's staff only</span>}
                                </td>
                                <td>
                                    {s.agents.length ? <span className="brk-hint">{s.agents.join(", ")}</span>
                                        : <span className="brk-badge brk-badge--amber">Nobody assigned</span>}
                                </td>
                                <td>
                                    {s.checkedIn > 0 ? <span className="brk-badge brk-badge--green">{s.checkedIn} checked in</span>
                                        : <span className="brk-hint">Nobody here</span>}
                                    {s.onCall > 0 && <div className="brk-hint">{s.onCall} on a tour</div>}
                                    {s.waiting > 0 && <div><span className="brk-badge brk-badge--orange">{s.waiting} buyer{s.waiting > 1 ? "s" : ""} waiting</span></div>}
                                </td>
                                <td>
                                    {editing?.siteId === s.siteId ? (
                                        <div className="brk-row-actions" style={{flexWrap: "wrap"}}>
                                            <label className="brk-hint">Radius (m)
                                                <input className="brk-input brk-input--sm" type="number" min={30} max={2000} style={{width: 80}}
                                                       value={editing.geofenceM} onChange={(e) => setEditing((x) => ({...x, geofenceM: e.target.value}))}/>
                                            </label>
                                            <label className="brk-hint">From
                                                <input className="brk-input brk-input--sm" type="time" value={editing.tourStart}
                                                       onChange={(e) => setEditing((x) => ({...x, tourStart: e.target.value}))}/>
                                            </label>
                                            <label className="brk-hint">To
                                                <input className="brk-input brk-input--sm" type="time" value={editing.tourEnd}
                                                       onChange={(e) => setEditing((x) => ({...x, tourEnd: e.target.value}))}/>
                                            </label>
                                            <label className="brk-hint">Max waiting
                                                <input className="brk-input brk-input--sm" type="number" min={1} max={10} style={{width: 60}}
                                                       value={editing.maxWaiting} onChange={(e) => setEditing((x) => ({...x, maxWaiting: e.target.value}))}/>
                                            </label>
                                        </div>
                                    ) : (
                                        <>
                                            {s.settings.toursPaused ? <span className="brk-badge brk-badge--red">Paused</span>
                                                : <span className="brk-badge brk-badge--green">On</span>}
                                            <div className="brk-hint">
                                                {hhmm(s.settings.tourStart)}–{hhmm(s.settings.tourEnd)} · within {s.settings.geofenceM >= 1000
                                                    ? `${Math.round(s.settings.geofenceM / 1000)} km` : `${s.settings.geofenceM} m`} · up to {s.settings.maxWaiting} waiting
                                            </div>
                                        </>
                                    )}
                                </td>
                                <td>
                                    {editing?.siteId === s.siteId ? (
                                        <span className="brk-row-actions">
                                            <button type="button" className="brk-link" disabled={busy === s.siteId}
                                                    onClick={() => save(s, {
                                                        geofenceM: Number(editing.geofenceM), tourStart: editing.tourStart, tourEnd: editing.tourEnd,
                                                        maxWaiting: Number(editing.maxWaiting), toursPaused: s.settings.toursPaused,
                                                    })}>Save</button>
                                            <button type="button" className="brk-link" disabled={busy === s.siteId}
                                                    onClick={() => save(s, {toursPaused: s.settings.toursPaused})}>Use defaults</button>
                                            <button type="button" className="brk-link" onClick={() => setEditing(null)}>Cancel</button>
                                        </span>
                                    ) : (
                                        <span className="brk-row-actions" style={{flexDirection: "column", alignItems: "flex-start"}}>
                                            <button type="button" className={`brk-link${s.settings.toursPaused ? "" : " brk-link--danger"}`}
                                                    disabled={busy === s.siteId} onClick={() => togglePause(s)}>
                                                {s.settings.toursPaused ? "Resume tours" : "Pause tours"}
                                            </button>
                                            <button type="button" className="brk-link" onClick={() => setEditing({
                                                siteId: s.siteId, geofenceM: Math.min(s.settings.geofenceM, 2000),
                                                tourStart: hhmm(s.settings.tourStart), tourEnd: hhmm(s.settings.tourEnd),
                                                maxWaiting: s.settings.maxWaiting,
                                            })}>Edit rules</button>
                                        </span>
                                    )}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
            <p className="brk-hint" style={{marginTop: 14}}>
                Paused listings ring nobody: buyers are offered a time instead. Radius is how close an agent must be to check in.
            </p>
        </div>
    );
};

export default SitesTable;
